/**
 * ATTRIBUTE VOCABULARY CONFLICTS
 *
 * ⛔ "CONFLICT", NOT "GAP", AND THE NAME IS LOAD-BEARING. A "gap" implies something is MISSING FROM
 * THE VOCABULARY and steers the reader toward adding the value. The largest real rows show the
 * opposite: gender="male" across 3,007 products is not a gap — it is a vocabulary that admits ONE
 * value. If this screen pushed toward "add this value", it would launder broken research into
 * canonical vocabulary one approval at a time, which is the exact failure the underlying row warns
 * about. A CONFLICT is resolvable in BOTH directions and this page must not make "the vocabulary is
 * wrong" the harder read.
 *
 * ⚠️ Distinct from Demand Synthesis's "vocabulary gaps", which is the DEMAND side. Different thing,
 * deliberately different label.
 *
 * ⛔ THIS SCREEN DECIDES NOTHING. It reports what the vocabulary does not admit. Whether a value is a
 * synonym, an error, or a legitimate new member is a human judgement; code that guessed would
 * reproduce the defect this view exists to expose.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Info, Database } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface ConflictRow {
  product_category: string;
  /**
   * #5968 r3: the category the vocabulary was READ from — the product's own path when it has canonical rows, else its
   * nearest ancestor (a split child such as "… > Soccer Shoes > turf" has none). Decisions are recorded under THIS
   * category; product_category stays the row's display identity. Absent on a backend that predates it.
   */
  schema_category?: string | null;
  schema_hops?: number | null;
  attribute_name: string;
  unrecognised_value: string;
  product_count: number;
  /** The category's admitted vocabulary — the synonym decision is "which of these does it map onto". */
  /** REQUIRED — the only source of synonym options. Absent means the screen cannot do its job. */
  allowed_values: string[];
}

/**
 * #5968 r4 (leg 1, minor): a decision is recorded under the VOCABULARY's category (schema_category, else the
 * product's own path). Two split children of one parent — "… > Soccer Shoes > turf" and "… > Soccer Shoes > firm"
 * — read the parent's vocabulary, so one decision on (attribute, value) settles BOTH rows on the server. Removing
 * only the clicked row left its sibling on screen, offering an already-resolved value to be decided again; the
 * server's answer then read as a failure. Undo is the mirror: every sibling the decision took off screen comes back.
 * ⛔ Pure, exported, and unit-tested — the previous filter was inline and only the clicked row's key was in view.
 */
// #5968 r5 (leg 2, minor): the server decides and suppresses CASE-INSENSITIVELY and trim-tolerantly
// (VocabularyConflictDecisionService: normalizeVariantForStorage(value).toLowerCase()), so "AG" and "ag" under one
// vocabulary are ONE conflict server-side. The key must say the same, or deciding "AG" leaves "ag" actionable on screen.
// Rows keep their original spelling; only the KEY is normalised.
export const normalisedVariant = (value: string) => String(value ?? '').trim().toLowerCase();
// #5968 r6 (leg 1, minor): the attribute half too — the server's specRowWhereSql matches attribute names by
// btrim(regexp_replace(lower(name), '[^a-z0-9]+', '_', 'g'), '_'), so "GROUND" and "ground" are one attribute there.
export const normalisedAttribute = (name: string) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
export const vocabularyKey = (r: ConflictRow) =>
  JSON.stringify([r.schema_category ?? r.product_category, normalisedAttribute(r.attribute_name), normalisedVariant(r.unrecognised_value)]);
/** The rows a decision on `decided` settles: every row recorded under the same vocabulary, the clicked one included. */
export const rowsSettledBy = (rows: ConflictRow[], decided: ConflictRow): ConflictRow[] =>
  rows.filter((x) => vocabularyKey(x) === vocabularyKey(decided));
export const rowsAfterDecision = (rows: ConflictRow[], decided: ConflictRow): ConflictRow[] =>
  rows.filter((x) => vocabularyKey(x) !== vocabularyKey(decided));
/** Undo restores every settled row that is not already back (by row identity), appended in their retained order. */
export const rowsAfterUndo = (rows: ConflictRow[], settled: ConflictRow[]): ConflictRow[] => {
  const present = new Set(rows.map((x) => JSON.stringify([x.product_category, x.attribute_name, x.unrecognised_value])));
  const missing = settled.filter((x) => !present.has(JSON.stringify([x.product_category, x.attribute_name, x.unrecognised_value])));
  return missing.length === 0 ? rows : [...rows, ...missing];
};

/**
 * ⛔ A REFUSAL WITH NO UI PATH IS A DEAD END — an attribute the operator cannot act on, with no way to
 * learn why or what to do. Some refusals from this endpoint are DATA problems (duplicate spec-cache
 * rows) that cannot be resolved from this screen at all, so the message alone is not enough: the
 * operator needs both row identities and the thing that tracks the fix.
 * ⛔⛔ THE BRANCH IS ON `code`, NEVER ON THE MESSAGE. Matching prose is a lexical rule about the exact
 * strings its author happened to be looking at — the same instrument-shaped defect this feature has
 * now produced on five axes, and the one that made the previous duplicate audit read "3 rows".
 */
const CODE_DUPLICATE_SPEC_ROWS = 'duplicate_spec_rows';

interface DecisionFailure {
  message: string;
  code?: string;
  details?: {
    spec_names?: string[];
    attribute_name?: string;
    category_path?: string;
    tracking_task?: string;
  };
}

/**
 * ⛔⛔ ONE PLACE THAT TURNS A FAILED RESPONSE INTO A FAILURE — because the alternative is two copies,
 * and two copies is exactly the defect this PR has now been corrected for five times: the fix lands on
 * the handler the reviewer named and its twin sits untouched beside it. `decide` carried the code and
 * `undo` threw a bare Error, so a duplicate-row refusal reached from Undo rendered as the dead end
 * again — the same finding, in the same file, one function away.
 */
function failureFrom(data: any, status: number): Error & DecisionFailure {
  const message = data?.error || `Request failed (${status})`;
  return Object.assign(new Error(message), { message, code: data?.code, details: data?.details });
}

interface Coverage {
  products_in_scope: number;
  products_enriched: number;
  products_compared: number;
  products_with_gaps: number;
  products_excluded_by_defaults: number;
  counts_are_lower_bound?: boolean;
  lower_bound_reason?: string;
}

export default function AttributeVocabularyConflicts() {
  const { selectedSite } = useSite();
  const [rows, setRows] = useState<ConflictRow[]>([]);
  // #5968 r4: the latest committed list, read by `decide` AFTER its await — the render closure's `rows` is the list
  // as of the press, and a sibling decision may have committed since. Same shape as currentSiteRef below.
  const rowsRef = useRef<ConflictRow[]>(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  /**
   * ⛔⛔ A SET, NOT A SLOT — AND IT IS THE SAME ROOT AS THE UNDO DEFECT BELOW.
   * `busyKey` held ONE key while several rows can be decided concurrently, so a second decision
   * overwrote the first row's busy state: the first row's spinner vanished and its controls
   * re-enabled while its request was still in flight. Whichever response landed last decided what
   * the screen said about BOTH.
   */
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const isBusy = (k: string) => busyKeys.has(k);
  const markBusy = (k: string, on: boolean) => setBusyKeys((prev) => {
    const next = new Set(prev);
    if (on) next.add(k); else next.delete(k);
    return next;
  });
  const [actionError, setActionError] = useState<DecisionFailure | null>(null);
  /**
   * ⛔⛔ THE SYNONYM SELECT IS CONTROLLED, AND THE RETRY PATH IS WHY.
   * It was uncontrolled (`defaultValue=""`), so after a FAILED decision it kept the value the
   * operator had chosen — and re-selecting the SAME canonical fires no onChange, so the retry did
   * NOTHING AT ALL. Not an error, not a second attempt: silence.
   * 🔑 A CONTROL THAT WORKS ONCE AND THEN SILENTLY DOES NOTHING ON THE SECOND PRESS, on the exact
   * path an operator reaches after a failure — which is precisely when they are least able to tell
   * whether the system heard them. Same family as every other silent no-op on this feature.
   * ⇒ Reset on completion, success or failure, so the next selection is always a real change event.
   */
  const [selection, setSelection] = useState<Record<string, string>>({});
  // ⛔ The undo affordance is keyed by the row it settled, so an operator can reverse the decision
  // they just took WITHOUT hunting for it. A vocabulary edit with no undo is a trapdoor, not a tool.
  /**
   * ⛔⛔ FOURTH DISTINCT PATH TO "THE REVERSAL YOU WERE PROMISED IS NO LONGER ON SCREEN".
   * This held ONE decision while several rows can be decided concurrently, so an overlapping second
   * response OVERWROTE the first decision's id and permanently removed its Undo — with RESPONSE
   * ORDERING deciding which one stayed undoable.
   * 🔑 Count them across this feature: undo unreachable on an inactive row, undo cleared by a site
   * change, undo dropped by a bare Error that lost the typed code, and now undo overwritten by a
   * concurrent decision. THAT IS NOT FOUR BUGS — IT IS WHAT HAPPENS WHEN A REVERSIBILITY GUARANTEE
   * LIVES IN ONE MUTABLE VARIABLE. Each fix was correct and the next path was already there.
   * ⇒ So the remedy is the SHAPE, not a fifth patch: every decision taken in this session is retained
   * and carries its own Undo, until it is undone or the site changes.
   */
  const [decisions, setDecisions] = useState<Array<{ key: string; id: string; attribute: string; category: string; label: string; rows: ConflictRow[] }>>([]);
  // ⛔ `reloadToken` IS GONE, NOT MERELY UNUSED. It existed to re-fetch after a decision; decisions
  // now update the local view from what the write returned, so nothing sets it. Leaving it would be
  // dead state that reads as a live reload mechanism — and its never-being-reset is precisely what
  // leaked the primary-routing flag across a whole session.

  /**
   * ⛔⛔ A COMPARISON BETWEEN TWO VALUES FROM THE SAME SOURCE IS NOT A CHECK.
   *
   * The stale-response guard used to read `requestedSite !== selectedSite?.id`, where BOTH sides came
   * from the SAME RENDER CLOSURE — `decide` is defined in the render body, so `selectedSite` inside it
   * is that render's snapshot and `requestedSite` was copied from it. Equal by construction. The guard
   * had the shape of a check, read as one in review, and COULD NOT DETECT THE CONDITION IT WAS ADDED
   * FOR. Third control on this PR added and unable to fail.
   * 🔑 AND I COPIED IT FROM THE LOADER TEN LINES AWAY WITHOUT ITS MECHANISM. The loader's `cancelled`
   * flag works because the effect CLEANUP mutates it when the dependency changes — a live signal from
   * outside the closure. I reproduced the shape and left the moving part behind, which is the same
   * failure as adopting a control and not executing it.
   * ⇒ A ref is the live identity: it is the SAME object across renders, so reading `.current` inside a
   * stale closure still returns the CURRENT site. That is what makes it capable of disagreeing.
   */
  const currentSiteRef = useRef<string | undefined>(selectedSite?.id);
  useEffect(() => { currentSiteRef.current = selectedSite?.id; }, [selectedSite?.id]);

  // ⛔ CLEAR DECISION STATE ON A SITE CHANGE — AND ONLY ON A SITE CHANGE. lastDecision survived it,
  // leaving the previous tenant's Undo button on screen while requests carried the NEW site id. It
  // would fail rather than cross the boundary, but a button that is visible and cannot work is the UI
  // half of the same honesty rule.
  // ⚠️ Its own effect, keyed on the site alone: putting this in the loader cleared the banner on the
  // reload a decision itself triggers, so Undo vanished the instant it became useful.
  useEffect(() => {
    setDecisions([]);
    setActionError(null);
  }, [selectedSite?.id]);

  useEffect(() => {
    if (!selectedSite?.id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      // ⛔ CLEAR PRIOR RESULTS BEFORE A NEW FETCH. Leaving the previous site's rows on screen while
      // the next site loads shows one site's conflicts under another site's name.
      setRows([]);
      setCoverage(null);
      try {
        const res = await authFetch(
          // ⛔ NO POST-WRITE VARIANT OF THIS REQUEST EXISTS ANY MORE. It used to append
          // `&afterWrite=true` so the reload was served by the primary — which made a client-controlled
          // switch out of a routing decision, and leaked permanently because this token is never reset.
          // ⇒ A decision now updates the local view from what the WRITE RETURNED, so there is no
          // read-after-write to route.
          `${API_URL}/product-enrichment/attribute-vocabulary-conflicts?siteId=${encodeURIComponent(selectedSite.id)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) {
          // ⛔ NO SILENT FALLBACK. An empty table on failure is indistinguishable from a clean
          // vocabulary — the one wrong answer this screen must never give.
          throw new Error(data.error || `Request failed (${res.status})`);
        }
        // ⛔⛔ AND THE SAME RULE FOR A MALFORMED SUCCESS. The throw above guards TRANSPORT failure —
        // the request fails. It does nothing for SHAPE failure — the request SUCCEEDS and is
        // malformed. A 200 carrying {success:true} and nothing else previously rendered
        // "No conflicts found" with no coverage block: the exact wrong answer the comment forbids,
        // produced by the two lines that used to sit here (`data.rows || []`, `data.coverage || null`).
        // 🔑 The guard covered the failure I was imagining and not the one I was not.
        // ⚠️ THIS FEATURE EXISTS BECAUSE AN EMPTY RESULT READ AS "NO VOCABULARY GAPS". The screen
        // built to fix that could render an empty result as "no conflicts" — the defect class
        // reproducing itself inside its own remedy.
        // ⛔ NOT defaulted to zeros: A ZEROED COVERAGE BLOCK IS A MORE CONVINCING FALSE CLEAN THAN A
        // MISSING ONE. Malformed means we do not know, and the screen must say so.
        if (!Array.isArray(data.rows)) {
          throw new Error('Malformed response: expected a list of conflicts.');
        }
        // ⛔ AND EACH ROW, NOT JUST THE CONTAINER. Validating only that rows IS AN ARRAY leaves a
        // malformed row to throw at render (product_count.toLocaleString()), which BYPASSES the
        // malformed-response state above and shows a blank screen instead of the error.
        // 🔑 THIRD TIME IN THIS FILE: the guard covered the level I was thinking about and not the one
        // below it. Array-ness was the level I had in mind; row shape is the level that renders.
        const badRow = data.rows.findIndex((r: any) =>
          !r || typeof r !== 'object'
          || typeof r.product_category !== 'string'
          || typeof r.attribute_name !== 'string'
          || typeof r.unrecognised_value !== 'string'
          || typeof r.product_count !== 'number'
          // ⛔⛔ allowed_values IS **REQUIRED**, NOT MERELY TYPE-CHECKED WHEN PRESENT. It is the ONLY
          // source of options for the synonym selector, so a response omitting it — or sending null —
          // rendered a WORKING-LOOKING SCREEN WITH AN EMPTY DROPDOWN and no reason given. The operator
          // sees a conflict they apparently cannot map and concludes the vocabulary has no members.
          // 🔑 THE TWIN OF THE DEFECT FIXED ONE FIELD OVER: tolerating absence with `|| []` at render
          // is the same false-clean as defaulting coverage to zeros. An optional field that the UI
          // cannot function without is not optional — it is a required field with a silent failure.
          // ⇒ Required here AND the `|| []` fallback at the render site is gone, so there is exactly
          // one place this can fail and it fails loudly.
          || !Array.isArray(r.allowed_values));
        if (badRow !== -1) {
          throw new Error(`Malformed response: conflict row ${badRow} is missing required fields.`);
        }
        // ⛔ ASSERT WHAT THIS PAGE RENDERS. NOTHING MORE. Not an enumeration of the API's shape — a
        // sixth field added upstream must never break a page that does not display it, and this page
        // must not develop a claim about fields it never reads.
        // ⚠️ products_excluded_by_defaults IS rendered even though its alert only appears when > 0:
        // NOT showing that alert is itself a rendered claim ("nothing was excluded"). A missing field
        // would make the page assert that silently, which is the same false-clean this screen exists
        // to prevent. A conditionally-rendered field is still rendered.
        // ⛔ by_category is DELIBERATELY ABSENT from this list, and adding it would be a mistake even
        // though it is always present today. This page does not read it; kg-readiness C1 does. A
        // validation placed in a consumer that does not USE the field protects a different consumer by
        // COINCIDENCE OF CODE LOCATION — it survives only until someone refactors a UI they believe
        // they own, and it vanishes silently because no test here would fail. C1's guarantee belongs
        // in C1's tests, and the API's shape in the API's tests. Three owners, three tests. (Ruled by
        // orchestrator 2026-08-07, who owns the C1 contract.)
        const RENDERED_COVERAGE_FIELDS = [
          'products_in_scope', 'products_enriched', 'products_compared',
          'products_with_gaps', 'products_excluded_by_defaults',
        ] as const;
        const missing = !data.coverage || typeof data.coverage !== 'object'
          ? RENDERED_COVERAGE_FIELDS.slice()
          : RENDERED_COVERAGE_FIELDS.filter((f) => typeof data.coverage[f] !== 'number');
        if (missing.length > 0) {
          // Coverage is what makes an empty list readable. Without it there is no honest way to
          // render this screen at all, so refuse rather than render a list that cannot be interpreted.
          throw new Error(`Malformed response: coverage is missing ${missing.join(', ')}.`);
        }
        setRows(data.rows);
        setCoverage(data.coverage);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [selectedSite?.id]);

  /**
   * ⛔ NO SEPARATOR THAT CAN APPEAR IN THE DATA. This joined three uncontrolled DB values with `|`, so
   * ("a|b", "c") and ("a", "b|c") produced the SAME key — and this key indexes busy state, the synonym
   * selection and the undo list, so a collision disables the wrong control, resets the wrong select, or
   * points an Undo at another row. Category paths and attribute names come from feeds; `|` is not a
   * character we get to reserve.
   * ⇒ JSON.stringify of the tuple: unambiguous by construction rather than by a convention about
   * which characters the data is assumed not to contain.
   */
  const rowKey = (r: ConflictRow) => JSON.stringify([r.product_category, r.attribute_name, r.unrecognised_value]);

  // ⛔ A DECISION IS NOT DONE WHEN THE REQUEST RETURNS 200 — it is done when the row LEAVES THE QUEUE.
  // So every decision re-reads the list. Trusting the response and removing the row locally would show
  // the operator a success the database may not agree with, which is the failure mode a decision tool
  // can least afford.
  const decide = async (r: ConflictRow, decision: 'synonym' | 'reject', canonical?: string) => {
    if (!selectedSite?.id) return;
    // ⛔ REQUEST IDENTITY. Clearing on site change is not enough: an IN-FLIGHT decision can resolve
    // afterwards and set state belonging to the previous tenant, showing their decision under the
    // newly selected site. The loader in this same file already captures-and-discards; this code did
    // not adopt the pattern that was ten lines away.
    const requestedSite = selectedSite.id;
    markBusy(rowKey(r), true);
    setActionError(null);
    try {
      const res = await authFetch(`${API_URL}/product-enrichment/attribute-vocabulary-conflicts/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          // #5968 r3: decide under the category the vocabulary came from (the backend resolves either way; this is the honest shape)
          categoryPath: r.schema_category ?? r.product_category,
          productCategory: r.product_category,
          attributeName: r.attribute_name,
          value: r.unrecognised_value,
          decision,
          canonical,
        }),
      });
      const data = await res.json();
      if (requestedSite !== currentSiteRef.current) return;   // the operator moved on; this answer is stale
      // ⛔ CARRY THE CODE, DO NOT RE-DERIVE IT. Throwing only the message would force the catch to
      // recognise this refusal by its wording, which is the dead end one layer in.
      if (!res.ok || !data.success) throw failureFrom(data, res.status);
      // ⛔ RESPONSE IDENTITY. Validating `success` alone let a malformed 200 render "Decision recorded"
      // AND an Undo button whose handler then returned silently because the id was absent — a control
      // that LOOKS like it worked, which is the same class as the row and request cases above.
      if (typeof data.decision?.id !== 'string' || data.decision.id === '') {
        throw new Error('Malformed response: the decision was not given an id, so it cannot be undone.');
      }
      // ⛔ THE LOCAL REMOVAL IS DRIVEN BY WHAT THE SERVER SAID IT DID, not by assuming the press worked.
      // Without this the client would be guessing that the row left the queue — the same "trust the
      // 200" reading this feature has refused everywhere else.
      if (data.resolved?.leaves_queue !== true) {
        throw new Error('Malformed response: the decision did not report that the row leaves the queue.');
      }
      // ⇒ APPENDED, never replaced. Replacing is what made response ORDERING decide which decision
      // stayed undoable. Re-deciding the SAME row supersedes its own entry; other rows are untouched.
      setDecisions((prev) => [
        ...prev.filter((d) => d.key !== rowKey(r)),
        {
          key: rowKey(r),
          id: data.decision.id,
          attribute: r.attribute_name,
          category: r.schema_category ?? r.product_category,   // #5968 r3: undo goes to the same category the decision was recorded under
          label: decision === 'synonym' ? `"${r.unrecognised_value}" mapped to "${canonical}"` : `"${r.unrecognised_value}" marked wrong`,
          // #5968 r4: retained so undo can restore them without asking the database again — ALL the rows this
          // decision settles (split-child siblings sharing the vocabulary), read from the same list the removal reads.
          rows: rowsSettledBy(rowsRef.current, r),
        },
      ]);
      // ⇒ THE ROW LEAVES THE QUEUE LOCALLY, on the server's own statement. No reload: re-asking a
      // replica what the primary just told us is what produced the read-after-write hole, and routing
      // that read at the primary is what produced the availability one.
      // #5968 r4: and so does every sibling recorded under the same vocabulary — the server settled them together.
      setRows((prev) => rowsAfterDecision(prev, r));
    } catch (e: any) {
      if (requestedSite === currentSiteRef.current) {
        setActionError({ message: e.message, code: e.code, details: e.details });
      }
    } finally {
      // ⛔ THE `finally` IS A STATE UPDATE TOO. Clearing busyKey unconditionally lets a slow response
      // from the PREVIOUS site clear the spinner of an action the operator started after switching.
      if (requestedSite === currentSiteRef.current) {
        markBusy(rowKey(r), false);
        // ⇒ Cleared on BOTH outcomes. On success the row leaves the queue anyway; on FAILURE this is
        // what makes re-picking the same canonical a real change event instead of a silent no-op.
        setSelection((prev) => {
          if (!(rowKey(r) in prev)) return prev;
          const next = { ...prev };
          delete next[rowKey(r)];
          return next;
        });
      }
    }
  };

  const undo = async (d: { key: string; id: string; attribute: string; category: string; label: string; rows: ConflictRow[] }) => {
    if (!selectedSite?.id || !d?.id) return;
    const requestedSite = selectedSite.id;
    markBusy(d.key, true);
    setActionError(null);
    try {
      const res = await authFetch(`${API_URL}/product-enrichment/attribute-vocabulary-conflicts/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id,
          categoryPath: d.category,
          attributeName: d.attribute,
          decisionId: d.id,
        }),
      });
      const data = await res.json();
      // ⛔ REQUEST IDENTITY, HERE TOO. `decide` captures-and-discards; this did not, so an in-flight
      // undo could resolve after a site change and set an error belonging to the previous tenant.
      // Third entry point in this file needing the same guard — proximity does not propagate a pattern.
      if (requestedSite !== currentSiteRef.current) return;
      if (!res.ok || !data.success) throw failureFrom(data, res.status);
      // ⛔ ONLY the decision that was undone leaves. Clearing them all would remove the Undo for
      // decisions nobody reversed — the same loss this collection was introduced to end.
      // ⛔⛔ UNDO IS NOT THE MIRROR OF THE WRITE. This used to restore the retained row on ANY
      // successful undo, on the reasoning that undo is symmetric. THAT SYMMETRY IS THE DEFECT:
      // supersession means undoing a decision does not necessarily unresolve the value — synonym A,
      // then synonym B, then undo B leaves A ACTIVE — so the screen would offer a resolved value to be
      // decided again.
      // 🔑 THE INVERSE OF "REMOVE THE ROW" IS NOT "RESTORE THE ROW"; IT IS "ASK WHAT THE STATE IS NOW".
      // The write path already stopped letting this client infer; undo kept inferring, one function
      // over. Same fix, applied to its twin.
      if (typeof data.resolved?.returns_to_queue !== 'boolean') {
        throw new Error('Malformed response: the undo did not report whether the value returns to the queue.');
      }
      setDecisions((prev) => prev.filter((x) => x.id !== d.id));
      if (data.resolved.returns_to_queue) {
        setRows((prev) => rowsAfterUndo(prev, d.rows));   // #5968 r4: the siblings the decision removed return with it
      }
    } catch (e: any) {
      if (requestedSite === currentSiteRef.current) {
        setActionError({ message: e.message, code: e.code, details: e.details });
      }
    } finally {
      // ⛔ THE `finally` IS A STATE UPDATE TOO. Clearing busyKey unconditionally lets a slow response
      // from the PREVIOUS site clear the spinner of an action the operator started after switching.
      if (requestedSite === currentSiteRef.current) markBusy(d.key, false);
    }
  };

  const visible = filter.trim()
    ? rows.filter((r) =>
        `${r.product_category} ${r.attribute_name} ${r.unrecognised_value}`
          .toLowerCase()
          .includes(filter.trim().toLowerCase()))
    : rows;

  if (!selectedSite?.id) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to see its attribute vocabulary conflicts.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Attribute Vocabulary Conflicts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Values products carry that their category&rsquo;s vocabulary does not admit. Each row is one
          decision, however many products it affects.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Could not load conflicts: {error}</AlertDescription>
        </Alert>
      )}

      {/* ⛔ THE DUPLICATE-ROW REFUSAL IS NOT AN OPERATOR ERROR AND MUST NOT READ AS ONE. "Decision
          failed: <sentence>" tells someone their press did not work; it does not tell them the press
          CANNOT work here, what is wrong, or who fixes it. Both stored spellings are named, because
          they are the identifying evidence — nobody has ever counted this population, so the first
          operator to hit this is the measurement, and they can only file it if they can see it. */}
      {actionError?.code === CODE_DUPLICATE_SPEC_ROWS ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <div>
              <strong>This attribute cannot be decided here — it needs a data fix.</strong>
            </div>
            <div>
              Its category vocabulary is stored in more than one spec-cache row, with names differing
              only in capitalisation
              {actionError.details?.spec_names?.length ? (
                <>
                  :{' '}
                  {actionError.details.spec_names.map((n, i) => (
                    <span key={n}>
                      {i > 0 && ', '}
                      <code className="px-1 py-0.5 rounded bg-muted font-mono text-xs">{n}</code>
                    </span>
                  ))}
                </>
              ) : null}
              . Enrichment reads only one of them and which one is not defined, so there is no correct
              row to write to and recording a decision would be a guess.
            </div>
            <div className="text-xs opacity-90">
              Nothing you can do on this screen resolves it. The duplicate rows have to be merged in the
              spec cache first — tracked by{' '}
              <code className="px-1 py-0.5 rounded bg-muted font-mono">
                {actionError.details?.tracking_task || 'task-p1w63gks3l'}
              </code>
              . Other attributes on this page are unaffected.
            </div>
          </AlertDescription>
        </Alert>
      ) : actionError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Decision failed: {actionError.message}</AlertDescription>
        </Alert>
      ) : null}

      {/* ⛔ UNDO IS PART OF THE DECISION, NOT A SETTING SOMEWHERE ELSE. A vocabulary edit an operator
          cannot reverse from where they made it is a trapdoor. */}
      {/* ⇒ ONE ROW PER DECISION, EACH WITH ITS OWN UNDO. A single banner meant a second decision
          silently replaced the first one's reversal — and which one survived was decided by response
          ORDERING. Every decision this session keeps its Undo until it is used or the site changes. */}
      {decisions.length > 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            {/* ⛔ THE OPERATOR-VISIBLE COPY CARRIES THE SAME CLAIM THE API DOES, so it gets the same
                correction — once, above the list. Undo removes variants BY VALUE, not by author: an
                identical variant added independently afterwards is removed with them. "This decision
                only" would be a guarantee the code cannot make, offered at the exact moment the
                operator is deciding whether it is safe to press. */}
            {/* ⛔ THE COPY TRACKS THE GUARANTEE, IN BOTH DIRECTIONS. It was narrowed one round ago
                because undo was lossy in the ambiguous case; undo now REFUSES there, so the promise
                is true again and the copy must not keep understating it — an unnecessarily scary
                warning is still a claim that does not match the code. */}
            {/* ⛔⛔ "AVAILABLE UNTIL YOU LEAVE THIS PAGE" IS THE HONEST SCOPE, AND IT IS SAID FIRST.
                The decision is recorded durably and the endpoint would honour it forever — but no
                endpoint returns persisted decision ids, and this list is page state, so after a
                reload there is no way to reach one. A capability with no button is not delivered,
                and after a refresh there is no button.
                🔑 A FILED ROW (task-ip1rrj7h3p) MAKES THE GAP TRACKED; IT DOES NOT MAKE THE CLAIM
                TRUE. Third time on this feature that the honest move was to shrink a promise rather
                than defend it. */}
            <div className="text-muted-foreground text-xs">
              <strong>Undo is available until you leave this page.</strong> The decision itself is
              recorded permanently, but it cannot be reversed from here after a reload. Undo reverses
              this decision only, and is refused rather than applied if anything else has changed the
              vocabulary since.
            </div>
            {decisions.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-4">
                <span>Decision recorded: {d.label}.</span>
                <Button size="sm" variant="outline" onClick={() => undo(d)} disabled={isBusy(d.key)}>
                  Undo
                </Button>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* ⛔ COVERAGE IS SHOWN WHETHER OR NOT THERE ARE ROWS — it is what makes an empty table
          readable. "The vocabulary admits everything" and "nothing was compared" are different
          facts and only one of them is good news. */}
      {coverage && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" /> What was measured
            </CardTitle>
            <CardDescription>
              {coverage.products_compared.toLocaleString()} of{' '}
              {coverage.products_enriched.toLocaleString()} enriched products were compared against a
              live category vocabulary
              {coverage.products_enriched > 0 && (
                <> ({Math.round((coverage.products_compared / coverage.products_enriched) * 100)}%)</>
              )}.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
              <span>In scope: <strong>{coverage.products_in_scope.toLocaleString()}</strong></span>
              <span>Enriched: <strong>{coverage.products_enriched.toLocaleString()}</strong></span>
              <span>Compared: <strong>{coverage.products_compared.toLocaleString()}</strong></span>
              <span>With conflicts: <strong>{coverage.products_with_gaps.toLocaleString()}</strong></span>
            </div>
            {/* ⛔ COVERAGE IS AS OF THE LAST LOAD, AND SAYS SO ONCE IT STOPS BEING. Decisions update the
                list locally rather than re-querying, so these counts do not move until the next
                ordinary load. That drift is the accepted cost of not re-asking a replica what the
                primary just told us — and an operator comparing a shrinking list against a static
                count must be told which one is stale, or the numbers look broken. */}
            {decisions.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Measured at the last load. {decisions.length} decision{decisions.length === 1 ? '' : 's'}{' '}
                taken since are reflected in the list below but not in these counts.
              </div>
            )}
            {coverage.products_compared === 0 && (
              <Alert className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Nothing was compared.</strong> An empty list below does not mean the
                  vocabulary is complete — no product in this site matched a category attribute with a
                  defined vocabulary, so no question was asked.
                </AlertDescription>
              </Alert>
            )}
            {coverage.products_excluded_by_defaults > 0 && (
              <Alert className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {coverage.products_excluded_by_defaults.toLocaleString()} enriched products were
                  excluded by the default scope and are not represented above.
                </AlertDescription>
              </Alert>
            )}
            {coverage.counts_are_lower_bound && (
              <p className="text-xs text-muted-foreground pt-1">
                Counts are a lower bound{coverage.lower_bound_reason ? ` — ${coverage.lower_bound_reason}` : ''}.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ⛔⛔ WHEN THE LOAD FAILED, DO NOT RENDER THIS CARD AT ALL. Refusing the malformed payload is
          only half the fix: with an error set and no rows, the card below still printed "No conflicts
          found in what was compared" — a reassuring empty state sitting directly under the error that
          says we could not measure anything. The operator reads the bottom of the screen.
          🔑 THE THROW MADE THE DATA HONEST; THE RENDER STILL LIED. Found by the test written for the
          layer above it, which is the only reason it was not shipped. */}
      {!error && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {rows.length.toLocaleString()} conflict{rows.length === 1 ? '' : 's'}
          </CardTitle>
          <CardDescription>
            A value here is not automatically wrong. Either the value is wrong, or the category&rsquo;s
            vocabulary is too narrow to admit it — an attribute whose vocabulary allows a single value
            will report almost every real value as a conflict.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Filter by category, attribute or value…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="mb-3 max-w-md"
          />

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Comparing stored values against category vocabularies…
            </div>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">
              {rows.length === 0
                ? 'No conflicts found in what was compared. Check the coverage above before reading that as complete.'
                : 'No conflicts match this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Category</th>
                    <th className="py-2 pr-4 font-medium">Attribute</th>
                    <th className="py-2 pr-4 font-medium">Value not admitted</th>
                    <th className="py-2 pr-4 font-medium text-right">Products</th>
                    <th className="py-2 font-medium">Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r, i) => (
                    <tr key={`${r.product_category}|${r.attribute_name}|${r.unrecognised_value}|${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-muted-foreground">
                        {r.product_category}
                        {r.schema_category && r.schema_category !== r.product_category && (
                          <div className="text-xs">vocabulary: {r.schema_category}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{r.attribute_name}</td>
                      <td className="py-2 pr-4"><Badge variant="outline">{r.unrecognised_value}</Badge></td>
                      <td className="py-2 pr-4 text-right tabular-nums">{r.product_count.toLocaleString()}</td>
                      <td className="py-2">
                        {/* ⛔ BOTH DIRECTIONS, EQUALLY REACHABLE. A conflict is resolvable two ways —
                            the value is wrong, or the vocabulary is too narrow — and if "it is a
                            synonym" were the easy click this screen would launder broken research
                            into canonical vocabulary one approval at a time. */}
                        <div className="flex items-center gap-2">
                          <select
                            className="border rounded px-2 py-1 text-xs bg-background max-w-[14rem]"
                            value={selection[rowKey(r)] ?? ''}
                            disabled={isBusy(rowKey(r))}
                            onChange={(e) => {
                              const picked = e.target.value;
                              setSelection((prev) => ({ ...prev, [rowKey(r)]: picked }));
                              if (picked) decide(r, 'synonym', picked);
                            }}
                            aria-label={`Map ${r.unrecognised_value} to an admitted value`}
                          >
                            <option value="">It&rsquo;s a synonym for&hellip;</option>
                            {/* ⛔ CHOSEN FROM THE ADMITTED VOCABULARY, never free text: a typo would
                                create a NEW canonical key that admits nothing. */}
                            {r.allowed_values.map((a) => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isBusy(rowKey(r))}
                            onClick={() => decide(r, 'reject')}
                          >
                            {isBusy(rowKey(r)) ? <Loader2 className="h-3 w-3 animate-spin" /> : 'It is wrong'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
